import { createAction } from 'redux-actions';
import {
  GET_RESERVATIONS_FOR_DAY,
  GET_RESERVATIONS_FOR_PREV_DAYS,
  REQUEST_FAIL,
  CHANGE_CHOSEN_DATE,
  UPDATE_STATUS_CONFIRMED,
  UPDATE_STATUS_DENIED,
  GET_CONFIRMED_RESERVATIONS,
  MARK_DID_ATTEND,
  MARK_DID_NOT_ATTEND,
  GET_TIMETABLE_SUCCESS,
  GET_SCHEDULE_SUCCESS,
  RESET_SCHEDULE_SUCCESS,
  CHANGE_CURRENT_DAY,
  CHANGE_CURRENT_MONTH,
  GET_CALENDAR_SUCCESS,
  FILTER_CALENDAR_SUCCESS,
  CHANGE_CONFIGURATION_DATE,
  CREATE_RESOURCE_SUCCESS,
  DELETE_RESOURCE_SUCCESS,
  EDIT_RESOURCE_SUCCESS,
  EDIT_NOT_CHECKED,
  GET_NEW_USERS_REGISTERED,
  UPDATE_DONOR,
  REFRESH_CONFIGURATION,
  SET_APP_TYPE_FILTER,
  SET_DONOR_STATUS_FILTER,
  SET_TITER_FILTER,
  SET_BLOOD_TYPE_FILTER,
  FETCHING_DAY,
  FETCHING_MONTH,
  FETCHING_SIDEBAR,
  FETCHING_SIDEBAR_ABORTED,
  FETCHING_DAY_ABORTED,
  FETCHING_MONTH_ABORTED,
  FETCHING_CONFIGURATION,
  SET_REFRESHED,
  CHANGE_RESERVATION_SUCCESS,
  SET_NOT_REFRESHED,
  SET_CONFIGURATION_REFRESHED,
  TOGGLE_RESOURCE_MENU,
  SET_RESOURCE,
  SET_IS_RESOURCE_EMPTY,
  TOGGLE_DELETE_RESOURCE_MODAL,
  TOGGLE_EDIT_RESOURCE_MODAL,
  RESET_DAILY_SCHEDULE,
  OVERWRITE_TIMESLOT,
  TOGGLE_OVERWRITE_MODAL,
  SET_FORCE_REFRESHED,
  SET_NOT_FORCE_REFRESHED,
  SET_IS_DIRECTLY_BOOKING,
  DONOR_DONATION_SITE_CHANGED,
} from './actionTypes';
import {
  getReservationsForDay,
  createReservation,
  cancelReservation,
  updateStatus,
  getTimetable,
  get,
  getCalendar,
  resourceCreate,
  resourceEdit,
  resourceDelete,
  getNewUsersRegistered,
  editEncryptedUser,
  moveDonorDonationSite,
  overwriteTimeslotAndBook,
  editTimeslot,
  createTimeslot
} from '../../services/fetch';
import { getValidIdToken } from "../auth/actions";
import { sendErrorLog } from "../logging/actions";
import {batch} from "react-redux";
import {DateTime} from "luxon";
import { abortPendingRequests, addPendingRequest, deletePendingRequestIfCurrent } from '../requests/actions';
import { getAbortController } from '../../services/helpers';
import { finishOverwriteTimeslot, toggleBookAppointmentModal } from '../timeslots/actions';
import { ACTIVE_BOOKING_EXISTS, FEATURE_DISABLED } from '../../constants/donors';

// ─── fetch-flag ownership (compare-and-clear) ────────────────────────────
// Every fetch "flight" that sets one of the reservation loading flags
// (isSidebarFetching / isDayViewFetching / isMonthViewFetching /
// isConfigurationFetching) gets a unique ownership token. The reducer stores
// the token in a companion *Owner field and only lets the CURRENT owner clear
// the flag. This closes the stranded/blinking-flag class permanently: when
// addPendingRequest aborts an in-flight same-key predecessor, the
// predecessor's release actions (abort/success/fail) arrive with a stale
// token and no-op instead of blanking the flag the successor just set.
// See reducer.js `releaseFlag`.
let fetchFlightSeq = 0;
const newFlightId = (label) => `${label}#${++fetchFlightSeq}`;

// Release-carrying actions keep their payload shape untouched (consumers read
// action.payload / payload.content as before); the releasing flight's token
// rides in meta: dispatch as action(payload, flightId).
const idMeta = (payload, requestId) => ({ requestId });

export const requestFail = createAction(REQUEST_FAIL, (err) => err, idMeta);
export const reservationsForDay = createAction(GET_RESERVATIONS_FOR_DAY, (res) => res, idMeta);
export const reservationsForPrevDays = createAction(GET_RESERVATIONS_FOR_PREV_DAYS, (res) => res, idMeta);
export const changeCurrentDate = createAction(CHANGE_CHOSEN_DATE);
export const updateStatusConfirmed = createAction(UPDATE_STATUS_CONFIRMED);
export const updateStatusDenied = createAction(UPDATE_STATUS_DENIED);
export const confirmedForDay = createAction(GET_CONFIRMED_RESERVATIONS);
export const didAttend = createAction(MARK_DID_ATTEND, (details) => details, idMeta);
export const didNotAttend = createAction(MARK_DID_NOT_ATTEND, (details) => details, idMeta);
export const getTimetableSuccess = createAction(GET_TIMETABLE_SUCCESS, (res) => res, idMeta);
export const getScheduleSuccess = createAction(GET_SCHEDULE_SUCCESS, (res) => res, idMeta);
export const resetScheduleSuccess = createAction(RESET_SCHEDULE_SUCCESS, (res) => res, idMeta);
export const changeDay = createAction(CHANGE_CURRENT_DAY);
export const changeMonth = createAction(CHANGE_CURRENT_MONTH);
export const getCalendarSuccess = createAction(GET_CALENDAR_SUCCESS, (res) => res, idMeta);
export const filterCalendarSuccess = createAction(FILTER_CALENDAR_SUCCESS);
export const changeConfigurationDateSuccess = createAction(CHANGE_CONFIGURATION_DATE);
export const createResourceSuccess = createAction(CREATE_RESOURCE_SUCCESS, (res) => res, idMeta);
export const deleteResourceSuccess = createAction(DELETE_RESOURCE_SUCCESS, (res) => res, idMeta);
export const editResourceSuccess = createAction(EDIT_RESOURCE_SUCCESS, (res) => res, idMeta);
export const editNotChecked = createAction(EDIT_NOT_CHECKED);
export const newUsersRegistered = createAction(GET_NEW_USERS_REGISTERED, (res) => res, idMeta);
export const editDonorSuccess = createAction(UPDATE_DONOR, (res) => res, idMeta);
// CMT-57 — payload: { donorId, moved, donationSiteIds, previousBloodbankId }
// after POST /v1/donors/{uid}/donation-site landed (moved = false for the
// server's `unchanged` answer)
export const donorDonationSiteChanged = createAction(DONOR_DONATION_SITE_CHANGED);
export const setCurrentAppTypeFilter = createAction(SET_APP_TYPE_FILTER);
export const setCurrentBloodTypeFilter = createAction(SET_BLOOD_TYPE_FILTER);
export const setCurrentDonorStatusFilter = createAction(SET_DONOR_STATUS_FILTER);
export const setCurrentTiterFilter = createAction(SET_TITER_FILTER);
export const refreshConfiguration = createAction(REFRESH_CONFIGURATION);
export const fetchingSidebar = createAction(FETCHING_SIDEBAR);
export const fetchingDay = createAction(FETCHING_DAY);
export const fetchingMonth = createAction(FETCHING_MONTH);
export const fetchingSidebarAborted = createAction(FETCHING_SIDEBAR_ABORTED);
export const fetchingDayAborted = createAction(FETCHING_DAY_ABORTED);
export const fetchingMonthAborted = createAction(FETCHING_MONTH_ABORTED);
export const fetchingConfiguration = createAction(FETCHING_CONFIGURATION);
export const setRefreshed = createAction(SET_REFRESHED);
export const changeReservationSuccess = createAction(CHANGE_RESERVATION_SUCCESS, (res) => res, idMeta);
export const setNotRefreshed = createAction(SET_NOT_REFRESHED);
export const setForceRefreshed = createAction(SET_FORCE_REFRESHED);
export const setNotForceRefreshed = createAction(SET_NOT_FORCE_REFRESHED);
export const setConfigurationRefreshed = createAction(SET_CONFIGURATION_REFRESHED);
export const setResourceMenuIsOpen = createAction(TOGGLE_RESOURCE_MENU);
export const setSelectedResource = createAction(SET_RESOURCE);
export const setIsResourceEmpty = createAction(SET_IS_RESOURCE_EMPTY);
export const toggleDeleteResourceModal = createAction(TOGGLE_DELETE_RESOURCE_MODAL);
export const toggleEditResourceModal = createAction(TOGGLE_EDIT_RESOURCE_MODAL);
export const resetDailySchedule = createAction(RESET_DAILY_SCHEDULE);
export const overwritingTimeslot = createAction(OVERWRITE_TIMESLOT)
export const toggleOverwriteModal = createAction(TOGGLE_OVERWRITE_MODAL)
export const setIsDirectlyBooking = createAction(SET_IS_DIRECTLY_BOOKING);

export const bookAppointment = (startDate, timeslotId, userId, bloodbankId, override = false) => (dispatch) => {
  const flightId = newFlightId('bookAppointment');
  dispatch(fetchingDay(flightId));
  dispatch(fetchingSidebar(flightId));

  return createReservation(`/v1/bloodbanks/${bloodbankId}/reservations`, startDate, timeslotId, userId, override)
  .then(() => {
    return dispatch(changeReservationSuccess(undefined, flightId));
  })
  .then(() => {
    return dispatch(refreshConfiguration());
  })
  .catch(err => {
    const isPreCheck = err && err.body && err.body.error === 'failed_checks';
    if (isPreCheck) {
      err._suppressToast = true;
    }
    dispatch(requestFail(err, flightId));
    throw err;
  });
};

export const overwriteTimeslot = (timeslotWithExsceptions, updatedTimeslot, currentBloodbank, userId, bookDate) => (dispatch) => {
  dispatch(overwritingTimeslot({ isTimeslotOverwriting: true, overwritingError: null }));

  editTimeslot(timeslotWithExsceptions, currentBloodbank)
    .then(() => {
      return createTimeslot(updatedTimeslot, currentBloodbank)
    })
    .then((res) => {
      return createReservation(`/v1/bloodbanks/${currentBloodbank}/reservations`, bookDate, res?.content.timeslotId, userId)
    })
    .then(() => {
      return batch(() => {
        dispatch(overwritingTimeslot({ isTimeslotOverwriting: false }));
        dispatch(finishOverwriteTimeslot());
      })
    })
    .then(() => {
      return dispatch(refreshConfiguration());
    })
    .catch(err => {
      return dispatch(overwritingTimeslot({ isTimeslotOverwriting: false, overwritingError: err.message }));
    });
}

export const cancelAppointmentBooking = (bloodbankId, reservationId, userId, reservationTimestamp, bloodbankName) => (dispatch) => {
  const flightId = newFlightId('cancelAppointmentBooking');
  dispatch(fetchingDay(flightId));
  dispatch(fetchingSidebar(flightId));

  return cancelReservation(`/v1/bloodbanks/${bloodbankId}/reservations/${reservationId}/cancel`, reservationId, userId, reservationTimestamp, bloodbankName)
  .then(() => {
    return dispatch(changeReservationSuccess(undefined, flightId));
  })
  .then(() => {
    return dispatch(refreshConfiguration());
  })
  .catch(err => {
    return dispatch(requestFail(err, flightId));
  });
};

export const getDailyReservations = (date, appointmentType, bloodType, donorStatus, bloodbankId, isLoaderNotNeeded) => (dispatch) => {
  // Guard before any flag is set (same pattern as getTimetableRequest): a
  // falsy bloodbankId would fetch /v1/bloodbanks//reservations -> 404.
  if (!bloodbankId) return;
  const functionKey = `getDailyReservations`;
  const flightId = newFlightId(functionKey);
  // silent refresh (isLoaderNotNeeded): no flag is set, so this flight never
  // owns the sidebar flag — its releases below are owner-checked no-ops
  if (!isLoaderNotNeeded) dispatch(fetchingSidebar(flightId));
  const { signal, abortController } = getAbortController();

  dispatch(addPendingRequest({ id: functionKey, controller: abortController }));

  getReservationsForDay(`/v1/bloodbanks/${bloodbankId}/reservations?date=${date}&status=confirmed&appointmentType=${appointmentType}&bloodType=${bloodType}&donorStatus=${donorStatus}`, { signal })
    .then((res) => dispatch(reservationsForDay(res, flightId)))
    .catch((err) => {
      if (err.name === 'AbortError') {
        // an aborted request must release the loading flag it set — but only
        // its own: if a same-key successor already took ownership of the flag,
        // this release is a no-op (compare-and-clear in the reducer)
        if (!isLoaderNotNeeded) dispatch(fetchingSidebarAborted(flightId));
        return;
      }
      dispatch(requestFail(err, flightId));
    })
    .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
};

export const getPrevReservations = (date, appointmentType, bloodType, donorStatus, bloodbankId, isLoaderNotNeeded) => (dispatch) => {
  if (!bloodbankId) return;
  const functionKey = `getPrevReservations`;
  const flightId = newFlightId(functionKey);
  if (!isLoaderNotNeeded) dispatch(fetchingSidebar(flightId));
  const { signal, abortController } = getAbortController();
  dispatch(addPendingRequest({ id: functionKey, controller: abortController }));

  // ?through= keeps the legacy un-bounded semantics: everything up to END of that day
  get(`/v1/bloodbanks/${bloodbankId}/reservations?through=${date}&status=confirmed&appointmentType=${appointmentType}&bloodType=${bloodType}&donorStatus=${donorStatus}`, { signal })
    .then((res) => dispatch(reservationsForPrevDays(res, flightId)))
    .catch((err) => {
      if (err.name === 'AbortError') {
        if (!isLoaderNotNeeded) dispatch(fetchingSidebarAborted(flightId));
        return;
      }
      dispatch(requestFail(err, flightId));
    })
    .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
};

export const getNewUsers = (bloodbankId, isLoaderNotNeeded) => (dispatch) => {
  if (!bloodbankId) return;
  const functionKey = 'getNewUsers';
  const flightId = newFlightId(functionKey);
  if (!isLoaderNotNeeded) dispatch(fetchingSidebar(flightId));
  // in-flight guard: Dashboard fires this from two Firestore listeners and a
  // needRefresh effect — a newer call aborts the predecessor (same pattern as
  // getDailyReservations), so a listener burst lands exactly one request
  const { signal, abortController } = getAbortController();
  dispatch(addPendingRequest({ id: functionKey, controller: abortController }));

  getNewUsersRegistered(`/v1/bloodbanks/${bloodbankId}/donors?status=new_registration`, { signal })
  .then((res) => dispatch(newUsersRegistered(res, flightId)))
  .catch((err) => {
    if (err.name === 'AbortError') {
      // release only the flag this flight owns (owner-checked in the reducer)
      if (!isLoaderNotNeeded) dispatch(fetchingSidebarAborted(flightId));
      return;
    }
    dispatch(requestFail(err, flightId));
  })
  .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
}

// Resolves with the outcome — true once the PUT landed, false after a
// failure (already logged and surfaced through requestFail here, so nothing
// rejects) — for the callers that must sequence further work on it (CMT-57:
// the donation-site move runs only after the other edits are saved).
// Fire-and-forget callers are unaffected.
export const updateDonor = (user) => async (dispatch) => {
  const flightId = newFlightId('updateDonor');
  dispatch(fetchingDay(flightId));
  dispatch(fetchingSidebar(flightId));

  const token = await dispatch(getValidIdToken());
  return editEncryptedUser(user.id, user, token)
  .then(() => {
    dispatch(editDonorSuccess(undefined, flightId));
    return true;
  })
  .catch(err => {
    dispatch(sendErrorLog(err.message, err.status, 'ekm', err.url));
    dispatch(requestFail(err, flightId));
    return false;
  });
}

// CMT-57: move a donor to another active donation site in the same HF — ONE
// call, the server validates (same HF, active site, no active booking) and
// bumps `updated`. Resolves with the outcome instead of throwing, so
// «Giverdetaljer» can keep its confirmation dialog open on the expected
// rejection:
//   { ok: true, response }                          moved (or `unchanged`)
//   { ok: false, reason: 'active_booking_exists' }  409 — donor untouched, no
//                                                   error surface here
//   { ok: false, reason: 'feature_disabled' }       409 (CMT-87) — the HF has
//                                                   «Bytt tappested» off;
//                                                   donor untouched, no error
//                                                   surface here either
//   { ok: false, reason: 'error', error }           anything else: logged +
//                                                   the standard requestFail
//                                                   surface, like updateDonor
// No fetch flag is set (no grid loader behind the modal); a successful move
// sets needRefresh so the day view and the donor list refetch — the list's
// Firestore trigger (newest-updated donor of the bank) does not fire when the
// moved donor was not that newest doc.
export const moveDonorToDonationSite = (donorId, bloodbankId) => async (dispatch) => {
  try {
    const response = await moveDonorDonationSite(donorId, bloodbankId);
    dispatch(donorDonationSiteChanged({
      donorId,
      moved: !(response && response.message === 'unchanged'),
      donationSiteIds: Array.isArray(response && response.donationSiteIds) ? response.donationSiteIds : null,
      previousBloodbankId: (response && response.previousBloodbankId) || null,
    }));
    return { ok: true, response };
  } catch (err) {
    const refusal = err && err.status === 409 && err.body ? err.body.error : null;
    if (refusal === ACTIVE_BOOKING_EXISTS || refusal === FEATURE_DISABLED) {
      return { ok: false, reason: refusal };
    }
    dispatch(sendErrorLog(err.message, err.status, 'ekm', err.url));
    dispatch(requestFail(err));
    return { ok: false, reason: 'error', error: err };
  }
};

export const changeDate = (date) => (dispatch) => {
  dispatch(abortPendingRequests())
  return dispatch(changeCurrentDate(date));
}

export const setDay = (date) => (dispatch) => {
  dispatch(abortPendingRequests())
  return dispatch(changeDay(date));
}

export const markDidAttend = (status, details, donorStatus, bloodbankId) => (dispatch) => {
  const flightId = newFlightId('markDidAttend');
  dispatch(fetchingDay(flightId));
  dispatch(fetchingSidebar(flightId));

  updateStatus(`/v1/bloodbanks/${bloodbankId}/reservations/${details.reservationId}/status`, status,  donorStatus)
  .then(() =>  dispatch(didAttend(details, flightId)))
  .catch((err => dispatch(requestFail(err, flightId))));
}

export const markDidNotAttend = (status, details, donorStatus, bloodbankId) => (dispatch) => {
  const flightId = newFlightId('markDidNotAttend');
  dispatch(fetchingDay(flightId));
  dispatch(fetchingSidebar(flightId));

  updateStatus(`/v1/bloodbanks/${bloodbankId}/reservations/${details.reservationId}/status`, status,  donorStatus)
  .then(() => dispatch(didNotAttend(details, flightId)))
  .catch((err => dispatch(requestFail(err, flightId))));
};

export const getTimetableRequest = (monthStart, bloodbankId, appType, bloodType, donorStatus) => dispatch => {
  // guard BEFORE setting the fetching flag: a falsy monthStart used to
  // dispatch fetchingMonth() and register a pending controller without ever
  // starting a fetch — isMonthViewFetching stayed true forever and blocked
  // every flag-gated loader/effect (the Pulse #15 stranded-flag class).
  // Same for bloodbankId: empty -> /v1/bloodbanks//timetable -> 404.
  if (!monthStart || !bloodbankId) return;
  const functionKey = `getTimetableRequest`;
  const flightId = newFlightId(functionKey);
  dispatch(fetchingMonth(flightId))
  const { signal, abortController } = getAbortController();
  dispatch(addPendingRequest({ id: functionKey, controller: abortController }));
  getTimetable(`/v1/bloodbanks/${bloodbankId}/timetable?month=${monthStart}&appointmentType=${appType}&bloodType=${bloodType}&donorStatus=${donorStatus}`,{signal})
    .then(res => dispatch(getTimetableSuccess(res, flightId)))
    .catch(err => dispatch(err.name === 'AbortError' ? fetchingMonthAborted(flightId) : requestFail(err, flightId)))
    .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
};


export const resetSchedule = (date, bloodbankId) => async (dispatch) => {
  // only set the loading flag when we will actually do the reset work —
  // a falsy date used to strand isDayViewFetching true forever
  // (no fetch flies here: the flag is set and synchronously released by
  // resetScheduleSuccess with the same ownership token)
  if(date){
    const flightId = newFlightId('resetSchedule');
    dispatch(fetchingDay(flightId));
    batch(() => {
      dispatch(resetScheduleSuccess(undefined, flightId))
      dispatch(abortPendingRequests())
      dispatch(changeDate(DateTime.fromMillis(date).toJSDate()));
      dispatch(setDay(date));
    })
  }
};
let changeFilterUniqueId = 0;
export const changeFilter = (dailyView, date, appType, bloodType, donorStatus, bloodbankId, isLoaderNotNeeded) => (dispatch,getState) => {
  if (!bloodbankId) return;

  const functionKey = `changeFilter.${dailyView}`;
  const flightId = newFlightId(functionKey);

  dispatch(setForceRefreshed());
  if (!isLoaderNotNeeded) {
    if (dailyView) {
      dispatch(fetchingDay(flightId));
    } else {
      dispatch(fetchingMonth(flightId));
    }
  }
  const requestId = ++changeFilterUniqueId;
  const { signal, abortController } = getAbortController();
    dispatch(addPendingRequest({ id: functionKey, controller: abortController }));
  if (dailyView) {
    get(`/v1/bloodbanks/${bloodbankId}/schedule?date=${date}&appointmentType=${appType}&bloodType=${bloodType}&donorStatus=${donorStatus}`, { signal })
      .then(res => {
        if (requestId === changeFilterUniqueId) {
          dispatch(getScheduleSuccess(res, flightId));
        } else if (!isLoaderNotNeeded) {
          // superseded by a newer changeFilter: the DATA is dropped, but this
          // flight must still settle the flag it set. Owner-checked, so if
          // the successor took the flag this is a no-op and the loader stays
          // up until the successor's own release.
          dispatch(fetchingDayAborted(flightId));
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          if (!isLoaderNotNeeded) dispatch(fetchingDayAborted(flightId));
          return;
        }
        dispatch(requestFail(err, flightId));
      })
      .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
  }
  else {
    // Fetch the month the calendar is actually showing: currentMonth is kept
    // current by setMonth() on every month navigation. It must NOT be derived
    // from currentDate — that is the selected DAY, which month navigation
    // does not change, so the listener-triggered refresh after navigating
    // refetched the PREVIOUS month and its response overwrote the freshly
    // loaded month's timetable (month view went blank ~1s after render).
    const month = getState().reservationReducer.currentMonth;

    getTimetable(`/v1/bloodbanks/${bloodbankId}/timetable?month=${month}&appointmentType=${appType}&bloodType=${bloodType}&donorStatus=${donorStatus}`, { signal })
      .then(res => {
        // requestId: a newer changeFilter supersedes this one. Month check:
        // drop a response that raced a month navigation — the navigation's
        // own getTimetableRequest owns the new month's data. Ownership and
        // these guards compose: the guards protect the DATA, the ownership
        // token protects the FLAG's lifetime.
        if (requestId === changeFilterUniqueId
            && month === getState().reservationReducer.currentMonth) {
          dispatch(getTimetableSuccess(res, flightId))
        } else if (!isLoaderNotNeeded) {
          // data dropped — settle our own flag (no-op if a successor owns it)
          dispatch(fetchingMonthAborted(flightId));
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          if (!isLoaderNotNeeded) dispatch(fetchingMonthAborted(flightId));
          return;
        }
        dispatch(requestFail(err, flightId));
      })
      .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
  }
};


export const setMonth = (date) => dispatch => {
  dispatch(changeMonth(date));
}

export const calendarGet = (date, bloodbankId, disableLoader) => dispatch => {
  const flightId = newFlightId('calendarGet');
  if (!disableLoader) {
    dispatch(fetchingConfiguration(flightId));
  }

  getCalendar(`/v1/bloodbanks/${bloodbankId}/configuration?date=${date}`)
    .then(res => dispatch(getCalendarSuccess(res, flightId)))
    .catch(err => dispatch(requestFail(err, flightId)))
}

export const filterCalendar = (date, donorType, bloodbankId) => dispatch => {
  getCalendar(`/v1/bloodbanks/${bloodbankId}/configuration?date=${date}`)
    .then(res => dispatch(filterCalendarSuccess(res)))
    .catch(err => dispatch(requestFail(err)))
}

export const changeConfigurationDate = (date) => dispatch => {
  dispatch(changeConfigurationDateSuccess(date));
}

export const createResource = (bloodbankId, resourceName) => dispatch => {
  const flightId = newFlightId('createResource');
  dispatch(fetchingConfiguration(flightId));

  resourceCreate(`/v1/bloodbanks/${bloodbankId}/resources`, {resourceName})
    .then(res => dispatch(createResourceSuccess(res, flightId)))
    .then(() => {
      return dispatch(refreshConfiguration())
    })
    .catch(err => dispatch(requestFail(err, flightId)));
}

export const checkIsResourceEmpty = (bloodbankId, resourceId) => dispatch => {
  dispatch(setIsResourceEmpty(null));

  get(`/v1/bloodbanks/${bloodbankId}/resources/${resourceId}/usage`)
    .then(res => dispatch(setIsResourceEmpty(res)))
    .catch(err => dispatch(requestFail(err)));
}

export const deleteResource = (bloodbankId, resourceId) => dispatch => {
  const flightId = newFlightId('deleteResource');
  dispatch(fetchingConfiguration(flightId));

  resourceDelete(`/v1/bloodbanks/${bloodbankId}/resources/${resourceId}`)
    .then(res => {
      return dispatch(deleteResourceSuccess(res, flightId))
    })
    .then(() => {
      return dispatch(refreshConfiguration())
    })
    .catch(err => {
      return dispatch(requestFail(err, flightId))
    });
}

export const editResource = (bloodbankId, resourceId, resourceName) => dispatch => {
  const flightId = newFlightId('editResource');
  dispatch(fetchingConfiguration(flightId));

  resourceEdit(`/v1/bloodbanks/${bloodbankId}/resources/${resourceId}`, { resourceName })
    .then(res => dispatch(editResourceSuccess(res, flightId)))
    .then(() => {
      return dispatch(refreshConfiguration())
    })
    .catch(err => dispatch(requestFail(err, flightId)));
}

export const setAppTypeFilter = currentFilter => dispatch => {
  dispatch(setCurrentAppTypeFilter(currentFilter));
};

export const setBloodTypeFilter = currentFilter => dispatch => {
  dispatch(setCurrentBloodTypeFilter(currentFilter));
};

export const setDonorStatusFilter = currentFilter => dispatch => {
  dispatch(setCurrentDonorStatusFilter(currentFilter));
};

export const setTiterFilter = currentFilter => dispatch => {
  dispatch(setCurrentTiterFilter(currentFilter));
};

export const changeToRefreshed = () => dispatch => {
  dispatch(setRefreshed());
};

export const changeConfigurationToRefreshed = () => dispatch => {
  dispatch(setConfigurationRefreshed());
};
